import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddInventoryInformation from "./AddInventoryInformation";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle } from "hooks/useTheme2FormPage";
import "./AddInventoryModern.css";
import { useGetInventoryId } from "api/inventory/service";

const Inventory = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const {
    inputStyle,
    contentClassName,
    formFieldsClassName,
    theme2FieldsCss,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "add-inventory",
    withSelectStyles: true,
    insetClass: "theme2-inventory-search-inset",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const vinFromUrl = searchParams.get("vin") || "";

  const [vin, setVin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { data, isLoading, isError } = useGetInventoryId(vin, submitting);
  const existingVin = useMemo(() => data?.inventory?.documentId, [data]);
  const isAddBusy = submitting || isLoading;

  useEffect(() => {
    if (data && submitting) {
      setSubmitting(false);
      if (!data?.exists) {
        navigate(`/add-inventory?vin=${data?.vin || vin}`);
      }
    }
  }, [data, submitting, navigate, vin]);

  useEffect(() => {
    if (isError && submitting) {
      setSubmitting(false);
    }
  }, [isError, submitting]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vin.trim()) {
      setError(t("addInventory.vinRequired"));
      return;
    }
    if (vin.length < 5) {
      setError(t("addInventory.vinMinLength"));
      return;
    }
    setError("");
    setSubmitting(true);
  };

  return (
    <div className={contentClassName}>
      <Theme2FormFieldsStyle css={theme2FieldsCss} />
      {!vinFromUrl ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            flexDirection: "column",
          }}
        >
          <Card
            className="add-inventory-modern-card"
            style={{ maxWidth: "42vw", margin: "0 auto", minWidth: "320px" }}
          >
            <CardHeader
              className="add-inventory-modern-header"
              style={{
                background: activeColorStyle.background,
                backgroundImage: activeColorStyle.backgroundImage,
              }}
            >
              <h2 className="add-inventory-title mb-0" style={{ fontSize: "1.5rem" }}>
                {t("addInventory.title")}
              </h2>
            </CardHeader>
            <CardBody>
              <div className="add-inventory-desc mb-4">{t("addInventory.description")}</div>
              <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                <FormGroup>
                  <Label htmlFor="vin" className="add-inventory-label">
                    {t("addInventory.vinNumber")}
                  </Label>
                  <Input
                    type="text"
                    id="vin"
                    name="vin"
                    value={vin}
                    onChange={(e) => setVin(e.target.value)}
                    placeholder={t("addInventory.vinPlaceholder")}
                    autoComplete="on"
                    className="form-control"
                    style={inputStyle}
                    disabled={isAddBusy}
                  />
                </FormGroup>
                {error && <div className="add-inventory-error">{error}</div>}
                <Button
                  type="submit"
                  className="add-inventory-btn"
                  style={{ ...activeColorStyle, color: "white" }}
                  block
                  disabled={isAddBusy}
                >
                  {isAddBusy ? (
                    <span className="d-inline-flex align-items-center justify-content-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                      {t("addInventory.adding")}
                    </span>
                  ) : (
                    t("addInventory.addButton")
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
          {data?.exists && (
            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <div
                className="add-inventory-error"
                style={{ display: "inline-block", marginBottom: "0.75rem" }}
              >
                Vehicle Exists in Available Inventory!
              </div>
              <div>
                <Button
                  onClick={() => navigate(`/inventory/${existingVin || vin}`)}
                  style={{ ...activeColorStyle, color: "white" }}
                >
                  Click to go to vehicle
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <AddInventoryInformation
          vin={vinFromUrl}
          activeColor={activeColor}
          shellThemeVariant={shellThemeVariant}
        />
      )}
    </div>
  );
};

export default Inventory;
